// src/app/employee-timesheets/services/employee-timesheet.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, map, expand, reduce, EMPTY } from 'rxjs';
import dayjs from 'dayjs';

import { DateUtils } from '../utils/date-utils';
import {
  ApiCompanyListItem,
  ApiDepartmentListItem,
  ApiPagedResponse,
  ApiWeeklyTimesheetResponse,
  DailyBookedHours,
  DailyLog,
  MultipleBookedHoursSameDayBody,
  RemainingHoursToFortyVm,
  WeeklyReportData,
  WeeklyReportFilters,
  WeeklyTimesheetResponse,
  YywwListItem,
} from '../models/timesheet.model';

@Injectable({ providedIn: 'root' })
export class EmployeeTimesheetService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  // =========================
  // Weekly report
  // =========================

  getWeeklyTimesheets(date: Date = new Date()): Observable<WeeklyTimesheetResponse> {
    const weekInfo = this.getWeekInfoFromDate(date);

    return this.getWeeklyTimesheetsByFilters({
      fullYearWeek: weekInfo.yyyyww,
    });
  }

  getWeeklyTimesheetsByFullYearWeek(fullYearWeek: string): Observable<WeeklyTimesheetResponse> {
    return this.getWeeklyTimesheetsByFilters({
      fullYearWeek,
    });
  }

  getWeeklyTimesheetsByFilters(filters: WeeklyReportFilters): Observable<WeeklyTimesheetResponse> {
    const params = this.buildWeeklyReportParams(filters);

    return this.http
      .get<ApiWeeklyTimesheetResponse>(`${this.baseUrl}/api/employee-time-logs/weekly-report`, {
        params,
      })
      .pipe(map((res) => this.normalizeWeeklyReport(res)));
  }

  private buildWeeklyReportParams(filters: WeeklyReportFilters): HttpParams {
    let params = new HttpParams();

    const fullYearWeek = String(filters.fullYearWeek ?? '').trim();
    const searchTerm = String(filters.searchTerm ?? '').trim();

    if (fullYearWeek) {
      params = params.set('FullYearWeek', fullYearWeek);
    }

    if (filters.companyIds?.length) {
      params = params.set('companyIds', filters.companyIds.join(','));
    }

    if (filters.departmentIds?.length) {
      params = params.set('departmentIds', filters.departmentIds.join(','));
    }

    if (typeof filters.employeeId === 'number') {
      params = params.set('employeeId', String(filters.employeeId));
    }

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return params;
  }

  private emptyWeeklyReportData(): WeeklyReportData {
    return {
      payableHoursSync: null,
      timeLogs: [],
      bookedhours: [],
      userSchedules: [],
      invalidUserClockLogs: [],
      dayOffRequests: [],
    };
  }

  private normalizeWeeklyReport(res: ApiWeeklyTimesheetResponse): WeeklyTimesheetResponse {
    if (res.status !== 'success') {
      return {
        status: 'error',
        data: this.emptyWeeklyReportData(),
      };
    }

    const timeLogs = (res.data.timeLogs ?? []).map((item) => {
      const user = item.user;
      const logs: DailyLog[] = user.logs ?? [];
      const totalHours = this.sumClockedHours(logs);
      const { logs: _logsOnUser, ...userWithoutLogs } = user;

      return {
        user: userWithoutLogs,
        logs,
        totalHours,
      };
    });

    const bookedhours = (res.data.bookedhours ?? []).map((item) => {
      const user = item.user;

      const bookedHours: DailyBookedHours[] = (user.bookedHours ?? []).map((day) => {
        const entries = day.bookedHours ?? day.bookedHour ?? [];

        return {
          date: day.date,
          bookedHours: entries,
          bookedHour: entries,
        };
      });

      const calculatedTotalHours = this.sumBookedHours(bookedHours);
      const totalHours =
        typeof item.totalHours === 'number' ? this.round2(item.totalHours) : calculatedTotalHours;

      const { bookedHours: _bookedHoursOnUser, ...userWithoutBookedHours } = user;

      return {
        user: userWithoutBookedHours,
        bookedHours,
        totalHours,
      };
    });

    return {
      status: 'success',
      data: {
        payableHoursSync: res.data.payableHoursSync ?? null,
        timeLogs,
        bookedhours,
        userSchedules: res.data.userSchedules ?? [],
        invalidUserClockLogs: res.data.invalidUserClockLogs ?? [],
        dayOffRequests: res.data.dayOffRequests ?? [],
      },
    };
  }

  private sumClockedHours(days: DailyLog[]): number {
    let total = 0;

    for (const day of days ?? []) {
      for (const timeLog of day.timeLogs ?? []) {
        total += typeof timeLog?.totalHours === 'number' ? timeLog.totalHours : 0;
      }
    }

    return this.round2(total);
  }

  private sumBookedHours(days: DailyBookedHours[]): number {
    let total = 0;

    for (const day of days ?? []) {
      const entries = day.bookedHours ?? day.bookedHour ?? [];

      for (const bookedHour of entries) {
        total += typeof bookedHour?.hours === 'number' ? bookedHour.hours : 0;
      }
    }

    return this.round2(total);
  }

  private round2(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private getWeekInfoFromDate(date: Date) {
    const d = dayjs(date);
    const year = d.isoWeekYear();
    const week = d.isoWeek();

    return {
      ...DateUtils['formatWeek'](year, week),
    };
  }

  // =========================
  // Companies / Departments
  // =========================

  getDepartmentsPage(
    page: number,
    pageSize = 200,
    searchTerm = '',
  ): Observable<ApiPagedResponse<ApiDepartmentListItem>> {
    let params = new HttpParams().set('pageNumber', String(page)).set('pageSize', String(pageSize));

    const term = String(searchTerm ?? '').trim();

    if (term) {
      params = params.set('searchTerm', term);
    }

    return this.http.get<ApiPagedResponse<ApiDepartmentListItem>>(
      `${this.baseUrl}/api/departments`,
      { params },
    );
  }

  getAllDepartments(pageSize = 200, searchTerm = ''): Observable<ApiDepartmentListItem[]> {
    return this.getAllPaged<ApiDepartmentListItem>((page) =>
      this.getDepartmentsPage(page, pageSize, searchTerm),
    ).pipe(
      map((items) =>
        [...items].sort((a, b) => {
          const nameCompare = String(a.name ?? '').localeCompare(String(b.name ?? ''));

          return nameCompare !== 0 ? nameCompare : a.id - b.id;
        }),
      ),
    );
  }

  getCompaniesPage(
    page: number,
    pageSize = 200,
    searchTerm = '',
  ): Observable<ApiPagedResponse<ApiCompanyListItem>> {
    let params = new HttpParams().set('pageNumber', String(page)).set('pageSize', String(pageSize));

    const term = String(searchTerm ?? '').trim();

    if (term) {
      params = params.set('searchTerm', term);
    }

    return this.http.get<ApiPagedResponse<ApiCompanyListItem>>(`${this.baseUrl}/api/companies`, {
      params,
    });
  }

  getAllCompanies(pageSize = 200, searchTerm = ''): Observable<ApiCompanyListItem[]> {
    return this.getAllPaged<ApiCompanyListItem>((page) =>
      this.getCompaniesPage(page, pageSize, searchTerm),
    ).pipe(
      map((items) =>
        [...items].sort((a, b) => {
          const nameCompare = String(a.name ?? '').localeCompare(String(b.name ?? ''));

          return nameCompare !== 0 ? nameCompare : a.id - b.id;
        }),
      ),
    );
  }

  private getAllPaged<T>(
    fetchPage: (page: number) => Observable<ApiPagedResponse<T>>,
  ): Observable<T[]> {
    return fetchPage(1).pipe(
      expand((res: ApiPagedResponse<T>) => {
        const nextPage = (res?.currentPage ?? 1) + 1;
        const totalPages = res?.totalPages ?? 1;

        return nextPage <= totalPages ? fetchPage(nextPage) : EMPTY;
      }),
      reduce((acc: T[], res: ApiPagedResponse<T>) => {
        if (res?.status === 'success') {
          acc.push(...(res.data ?? []));
        }

        return acc;
      }, []),
    );
  }

  // =========================
  // Auto-book remaining hours to 40
  // =========================

  buildRemainingHoursToForty(report: WeeklyTimesheetResponse): RemainingHoursToFortyVm[] {
    if (report.status !== 'success') {
      return [];
    }

    const timeLogMap = new Map<number, { name: string; clockedHours: number }>();
    const bookedMap = new Map<number, number>();

    for (const row of report.data.timeLogs ?? []) {
      const employeeId = row.user.id;
      const employeeName = `${row.user.firstName} ${row.user.lastName}`.trim();

      timeLogMap.set(employeeId, {
        name: employeeName,
        clockedHours: this.round2(row.totalHours ?? 0),
      });
    }

    for (const row of report.data.bookedhours ?? []) {
      bookedMap.set(row.user.id, this.round2(row.totalHours ?? 0));

      if (!timeLogMap.has(row.user.id)) {
        const employeeName = `${row.user.firstName} ${row.user.lastName}`.trim();

        timeLogMap.set(row.user.id, {
          name: employeeName,
          clockedHours: 0,
        });
      }
    }

    const results: RemainingHoursToFortyVm[] = [];

    for (const [employeeId, timeInfo] of timeLogMap.entries()) {
      const clockedHours = this.round2(timeInfo.clockedHours);
      const bookedHours = this.round2(bookedMap.get(employeeId) ?? 0);
      const combinedHours = this.round2(clockedHours + bookedHours);
      const remainingHours = this.round2(Math.max(0, 40 - combinedHours));

      results.push({
        employeeId,
        employeeName: timeInfo.name,
        clockedHours,
        bookedHours,
        combinedHours,
        remainingHours,
      });
    }

    return results.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }

  createMultipleBookedHoursForSameDay(body: MultipleBookedHoursSameDayBody): Observable<void> {
    return this.http
      .post(`${this.baseUrl}/api/booked-hours/multiple-for-the-same-day`, body)
      .pipe(map(() => void 0));
  }

  getWeekSundayByFullYearWeek(fullYearWeek: string): Observable<string> {
    const fw = String(fullYearWeek ?? '').trim();
    const year = fw.slice(0, 4);

    return this.getYywwsAll(year).pipe(
      map((weeks) => {
        const match = (weeks ?? []).find((week) => week.fullYearWeek === fw);

        return match?.endDate ?? '';
      }),
    );
  }

  // =========================
  // YYWW
  // =========================

  getYywwsPage(
    searchTerm: string,
    page: number,
    pageSize = 200,
  ): Observable<ApiPagedResponse<YywwListItem>> {
    const term = encodeURIComponent(String(searchTerm ?? '').trim());

    return this.http.get<ApiPagedResponse<YywwListItem>>(
      `${this.baseUrl}/api/yywws?searchTerm=${term}&pageNumber=${page}&pageSize=${pageSize}`,
    );
  }

  getYywwsAll(searchTerm: string, pageSize = 200): Observable<YywwListItem[]> {
    return this.getYywwsPage(searchTerm, 1, pageSize).pipe(
      expand((res: ApiPagedResponse<YywwListItem>) => {
        const nextPage = (res?.currentPage ?? 1) + 1;
        const totalPages = res?.totalPages ?? 1;

        return nextPage <= totalPages ? this.getYywwsPage(searchTerm, nextPage, pageSize) : EMPTY;
      }),
      reduce((acc: YywwListItem[], res: ApiPagedResponse<YywwListItem>) => {
        if (res?.status === 'success') {
          acc.push(...(res.data ?? []));
        }

        return acc;
      }, []),
      map((items) =>
        (items ?? [])
          .slice()
          .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate))),
      ),
    );
  }
}